import { useEffect } from "react";

import PropTypes from "prop-types";

import {
  objectTypes,
  actionNames,
  useTracker,
  eventTypes,
} from "@newsela/snitch";
import useLegacyArticleHeaderGet from "@/view/utils/useLegacyArticleHeaderGet";
import ErrorMessage from "@/errors/ErrorMessage";
import DistrictRestrictedContentError from "./DistrictRestrictedContentError";
import RestrictedContentNonStudentUserError from "./RestrictedContentNonStudentUserError";
import UnauthenticatedUserError from "./UnauthenticatedUserError";
import UnlicensedContentStudentUserError from "./UnlicensedContentStudentUserError";
import { errorTypes } from "./ViewPageError.constants";
import { rootCss } from "./ViewPageError.style";
import { getErrorType } from "./ViewPageError.utils";

const propTypes = {
  statusCode: PropTypes.number,
  userProperties: PropTypes.object,
  legacyArticleSlug: PropTypes.string,
  contentId: PropTypes.string,
};

const {
  GENERIC,
  NON_STUDENT_USER_RESTRICTED_CONTENT,
  STUDENT_USER_RESTRICTED_CONTENT,
  STUDENT_USER_UNLICENSED_CONTENT,
  UNAUTHENTICATED_USER_FREE_CONTENT,
  UNAUTHENTICATED_USER_RESTRICTED_CONTENT,
  AUTHENTICATED_USER_DISTRICT_RESTRICTED_CONTENT,
} = errorTypes;

const ViewPageError = ({
  legacyArticleSlug,
  userProperties,
  statusCode = 200,
  contentId,
}) => {
  const {
    isAuthenticatedUser: isAuthenticated,
    isStudent,
    isLiteUser,
  } = userProperties;
  const errorType = getErrorType(
    isStudent,
    isAuthenticated,
    statusCode,
    isLiteUser,
  );
  const { data: articleHeaderData, error } =
    useLegacyArticleHeaderGet(legacyArticleSlug);
  const { trackAction, getEventName } = useTracker();

  // Check for a 403 restricted content error and determine if its being thrown because of a
  // licensing mismatch between Langston and the Monolith. We do this by checking
  // if we get a successful fetch from the articleheader endpoint (used by the Monolith)
  // for the same article. If so, log the error to the console.
  if (
    errorType === NON_STUDENT_USER_RESTRICTED_CONTENT ||
    errorType === STUDENT_USER_RESTRICTED_CONTENT
  ) {
    if (articleHeaderData && !error) {
      console.error("Licensing mismatch error.");
    }
  }

  useEffect(() => {
    if (errorType) {
      trackAction({
        event_type: eventTypes.impression,
        event_name: getEventName(objectTypes.content, null, actionNames.error),
        dimension_properties: {
          content_id: contentId,
          description: `${errorType}`,
          error_code: `${statusCode}`,
        },
      });
    }
  }, [errorType]);

  const renderErrorMessage = () => {
    switch (errorType) {
      case GENERIC:
        return <ErrorMessage />;
      case UNAUTHENTICATED_USER_FREE_CONTENT:
      case UNAUTHENTICATED_USER_RESTRICTED_CONTENT:
        return <UnauthenticatedUserError />;
      case NON_STUDENT_USER_RESTRICTED_CONTENT:
        return <RestrictedContentNonStudentUserError />;
      case STUDENT_USER_UNLICENSED_CONTENT:
        return <UnlicensedContentStudentUserError />;
      case STUDENT_USER_RESTRICTED_CONTENT:
      case AUTHENTICATED_USER_DISTRICT_RESTRICTED_CONTENT:
        return <DistrictRestrictedContentError />;
      default:
        return null;
    }
  };

  return <div css={rootCss}>{renderErrorMessage()}</div>;
};

ViewPageError.propTypes = propTypes;

export default ViewPageError;
